import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// Titles separate the instruction with "||" or "‖".
const MAS_TITLE_SEPARATOR = /\|\||‖/;

// 852 holds every row as one JSON string, built with JSON.stringify:
// [{"muscle":"Left elbow flexors","grade":"1+"}]. 853 is never answered: it
// only supplies the grade texts for the picker, so nothing is saved for it.
const MAS_ROWS = 'question852';
const MAS_GRADE_OPTIONS = 'question853';

// The grade is the label in "(n)" at the start of an option, the same in both
// languages. Never "Item N", since the option order is not guaranteed, and
// never a number, since "1+" is not one. This is also the fixed rank: 0 is
// the best grade and 4 the worst, the opposite of the other scales.
export const MAS_GRADES = ['0', '1', '1+', '2', '3', '4'];
const MAS_GRADE_REGEX = /^\s*\(\s*([^)]+?)\s*\)\s*/;

export interface MasRow {
  muscle: string;
  grade: string;
}

interface MasGrade {
  label: string;
  description: string;
}

type MasRowGroup = FormGroup<{
  muscle: FormControl<string>;
  grade: FormControl<string>;
}>;

// The saved rows, or null when the answer is not a JSON list.
export function parseMasRows(value: unknown): MasRow[] | null {
  const text = String(value ?? '').trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed)
      ? parsed.map((row) => ({
          muscle: String(row?.muscle ?? ''),
          grade: String(row?.grade ?? ''),
        }))
      : null;
  } catch {
    return null;
  }
}

// Every saved row needs both the muscle and a grade.
function masRowsValidator(control: AbstractControl): ValidationErrors | null {
  const rows = parseMasRows(control.value);
  return rows?.every(
    (row) => !!row.muscle.trim() && MAS_GRADES.includes(row.grade),
  )
    ? null
    : { masRows: true };
}

@Component({
  selector: 'app-mas-questions',
  templateUrl: './mas-questions.component.html',
  styleUrl: './mas-questions.component.css',
  standalone: false,
})
export class MasQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the scale's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;

  rowsElement?: IQuestionnaireQuestion;
  gradeElement?: IQuestionnaireQuestion;
  grades: MasGrade[] = [];
  rows = new FormArray<MasRowGroup>([]);
  // Gives each row's grade buttons a radio group of their own.
  rowIds: number[] = [];
  private nextRowId = 0;
  // The JSON last written to 852, to tell it apart from a load or Revert.
  private savedJson = '';
  private subs = new Subscription();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.rowsElement = this.elements.find((el) => el.name === MAS_ROWS);
    this.gradeElement = this.elements.find(
      (el) => el.name === MAS_GRADE_OPTIONS,
    );
    this.grades = this.readGrades();

    const control = this.formGroup.get(MAS_ROWS);
    control?.addValidators(masRowsValidator);
    control?.updateValueAndValidity({ emitEvent: false });
    this.loadRows();

    this.subs.add(this.rows.valueChanges.subscribe(() => this.saveRows()));
    // Loading and Revert set 852; the rows follow it.
    this.subs.add(
      this.formGroup.valueChanges.subscribe(() => this.syncFromForm()),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isEditable(): boolean {
    return !!this.formGroup.get(MAS_ROWS)?.enabled;
  }

  getQuestionTitle(title?: string): string {
    return title?.split(MAS_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(MAS_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  addRow(): void {
    this.pushRow('', '', true);
  }

  removeRow(index: number): void {
    this.rowIds.splice(index, 1);
    this.rows.removeAt(index);
  }

  // Clicking the chosen grade again clears it.
  onGradeClick(row: MasRowGroup, label: string): void {
    if (!this.isEditable) return;

    if (row.controls.grade.value === label) {
      row.controls.grade.setValue('');
    }
  }

  // A half-filled row, once it has been left.
  getRowError(row: MasRowGroup): string | null {
    const { muscle, grade } = row.getRawValue();
    if (!row.touched || (!muscle.trim() && !grade)) return null;
    if (!muscle.trim()) return 'Enter the muscle';
    if (!grade) return 'Choose a grade';
    return null;
  }

  // The picker's grades from 853's options, in the fixed order 0 to 4.
  private readGrades(): MasGrade[] {
    return (this.gradeElement?.choices ?? [])
      .map((choice: IQuestionnaireChoice) => {
        const text = String(choice.text ?? '');
        const match = text.match(MAS_GRADE_REGEX);
        return {
          label: match?.[1] ?? '',
          description: text.slice(match?.[0].length ?? 0).trim(),
        };
      })
      .filter((grade) => MAS_GRADES.includes(grade.label))
      .sort(
        (a, b) => MAS_GRADES.indexOf(a.label) - MAS_GRADES.indexOf(b.label),
      );
  }

  private loadRows(): void {
    const value = String(this.formGroup.get(MAS_ROWS)?.value ?? '');
    // An answer that is not JSON is kept visible as a row's muscle.
    const saved = parseMasRows(value) ?? [{ muscle: value, grade: '' }];
    this.savedJson = value;

    this.rows.clear({ emitEvent: false });
    this.rowIds = [];
    for (const row of saved) {
      // A saved grade selects the option whose text starts with "(grade)".
      const grade = this.grades.some((g) => g.label === row.grade)
        ? row.grade
        : '';
      this.pushRow(row.muscle, grade, false);
    }
    if (!this.rows.length && this.isEditable) {
      this.pushRow('', '', false);
    }
    this.applyDisabled();
  }

  private pushRow(muscle: string, grade: string, emitEvent: boolean): void {
    this.rowIds.push(this.nextRowId++);
    this.rows.push(
      new FormGroup({
        muscle: new FormControl(muscle, { nonNullable: true }),
        grade: new FormControl(grade, { nonNullable: true }),
      }),
      { emitEvent },
    );
  }

  // Every row with anything in it, as one JSON string; empty when none.
  private saveRows(): void {
    const rows: MasRow[] = this.rows
      .getRawValue()
      .map((row) => ({ muscle: row.muscle.trim(), grade: row.grade }))
      .filter((row) => row.muscle || row.grade);

    this.savedJson = rows.length ? JSON.stringify(rows) : '';
    this.formGroup.get(MAS_ROWS)?.setValue(this.savedJson);
  }

  private syncFromForm(): void {
    const value = String(this.formGroup.get(MAS_ROWS)?.value ?? '');
    if (value !== this.savedJson) {
      this.loadRows();
    } else {
      this.applyDisabled();
    }
    this.cdr.detectChanges();
  }

  // A read-only form keeps its rows locked too.
  private applyDisabled(): void {
    if (!this.isEditable && this.rows.enabled) {
      this.rows.disable({ emitEvent: false });
    } else if (this.isEditable && this.rows.disabled) {
      this.rows.enable({ emitEvent: false });
    }
  }
}
